import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { makeStyles } from "@mui/styles";
import LoadingButton from "components/LoadingButton";
import { testSchedule } from "services/reportSchedulerService";
import { useSnackbar } from "hooks/useSnackbar";

const useStyles = makeStyles(() => ({
  modalTextField: {
    marginBottom: "16px",
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      backgroundColor: "#f9f9f9",
    },
    "& .MuiOutlinedInput-input": {
      padding: "12px 16px",
      fontSize: "14px",
    },
  },
  modalActions: {
    display: "flex",
    gap: "12px",
    padding: "0 24px 24px 24px",
    justifyContent: "flex-end",
  },
}));

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface TestReportModalProps {
  open: boolean;
  scheduleId: string | null;
  onClose: () => void;
}

const TestReportModal: React.FC<TestReportModalProps> = ({ open, scheduleId, onClose }) => {
  const classes = useStyles();
  const { showSnackbar } = useSnackbar();

  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      setEmail("");
      setEmailError("");
      setError("");
    }
  }, [open]);

  const validateEmail = (value: string): boolean => {
    if (!value.trim()) {
      setEmailError("Email is required");
      return false;
    }
    if (!EMAIL_REGEX.test(value.trim())) {
      setEmailError("Please enter a valid email address");
      return false;
    }
    setEmailError("");
    return true;
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    if (emailError) {
      validateEmail(value);
    }
    if (error) setError("");
  };

  const handleSubmit = async () => {
    if (!scheduleId) return;
    if (!validateEmail(email)) return;

    try {
      setLoading(true);
      setError("");
      const response = await testSchedule(scheduleId, { email: email.trim() });
      if (response?.status) {
        showSnackbar("Test report sent successfully", "success");
        setEmail("");
        onClose();
      }
    } catch (err: any) {
      console.error("Error sending test report:", err);
      setError(err?.message || "Failed to send test report");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !loading) {
      handleSubmit();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          minWidth: "400px",
        },
      }}
    >
      <DialogTitle>
        <Box
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            Send Test Report
          </Typography>
          <IconButton onClick={handleClose} size="small" disabled={loading}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ paddingTop: "16px", paddingBottom: "16px" }}>
        <Typography variant="body2" color="text.secondary" sx={{ marginBottom: "16px" }}>
          Enter an email address to receive a test report for this schedule.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ marginBottom: "16px" }}>
            {error}
          </Alert>
        )}

        <TextField
          fullWidth
          label="Email Address"
          type="email"
          value={email}
          onChange={handleEmailChange}
          onKeyPress={handleKeyPress}
          disabled={loading}
          error={!!emailError}
          helperText={emailError}
          placeholder="recipient@example.com"
          className={classes.modalTextField}
        />
      </DialogContent>
      <DialogActions className={classes.modalActions}>
        <LoadingButton
          onClick={handleClose}
          disabled={loading}
          variant="outlined"
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 600,
            minWidth: "100px",
          }}
        >
          Cancel
        </LoadingButton>
        <LoadingButton
          onClick={handleSubmit}
          loading={loading}
          variant="contained"
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 600,
            minWidth: "160px",
          }}
        >
          Send Test Report
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default TestReportModal;
